// Wrapper for the (Token Auth based) AWS API
// This is the new Auth Token based API that all legacy API services will ultimately be migrated too
import axios from 'axios';
import { generateRandomClientId } from 'common-state/src/helpers/utils';
import type {
  createCartPayload,
  CartActionsPayload,
  CartActionsResponse,
  DeliveryGroupsRequest,
  DeliveryGroupsResponse,
  UpdateDeliveryGroupBody,
  PlaceOrderRequest,
  PlaceOrderResponse,
  ValidateCaptureRequest,
  ValidateCaptureResponse,
  Setup3DSRequest,
  Setup3DSResponse,
} from 'tsconfig/types';

export type WebApiConfig = {
  urlPrefix: string;
  extraHeaders?: Record<string, string>;
  host: string;
  countryBrand: string;
};

export const initWebApi = (config: WebApiConfig): ReturnType<typeof createWebApi> => createWebApi(config);

function createWebApi(config: WebApiConfig) {
  async function asyncRequest(
    requestType: string,
    url: string,
    data = {},
    requestHeaders: Record<string, string> = {},
    returnFullResponse = false,
  ): Promise<any> {
    const requestConfig = {
      method: requestType,
      url: `${config.urlPrefix}/webapi/${url}`,
      headers: {
        siteorigin: config.host,
        'x-dw-brand-domain': config.host,
        'x-dw-client-id': sessionStorage.getItem('sessionUID') || generateRandomClientId(),
        ...config.extraHeaders,
        ...requestHeaders,
      },
      data,
    };

    if (data) {
      requestConfig.data = data;
    }

    const response = await axios.request(requestConfig);
    return returnFullResponse ? response : response.data;
  }

  return {
    CartPreferences: {
      createNewCart: async (data?: createCartPayload) => asyncRequest('POST', `carts/me`, data),
      addItemToCart: async (params: any) => asyncRequest('POST', `carts/me/${params.cartId}/items`, params.data),
      deleteItemFromCart: async (params: any) =>
        asyncRequest('DELETE', `carts/me/${params.cartId}/items/${params.lineItemId}?version=${params.version}`),
      updateCartItem: async (params: any, data: any) => asyncRequest('PUT', `carts/me/${params.cartId}`, data),
      getMyCart: async (params: any) => asyncRequest('GET', `carts/me/${params.cartId}`),
      cartActions: async (params: { cartId: string; data: CartActionsPayload }): Promise<CartActionsResponse> =>
        asyncRequest('POST', `carts/me/${params.cartId}/actions`, params.data),
    },
    Checkout: {
      setDeliveryGroups: async (
        cartId: string,
        data: DeliveryGroupsRequest | UpdateDeliveryGroupBody,
      ): Promise<DeliveryGroupsResponse> => asyncRequest('POST', `carts/me/${cartId}/delivery-groups`, data),
      updateDeliveryGroups: async (
        cartId: string,
        deliveryGroupKey: string,
        data: UpdateDeliveryGroupBody,
      ): Promise<DeliveryGroupsResponse> =>
        asyncRequest('PUT', `carts/me/${cartId}/delivery-groups/${deliveryGroupKey}`, data),
      placeOrder: async (data: PlaceOrderRequest, idempotencyKey: string): Promise<PlaceOrderResponse> => {
        const axiosResponse = await asyncRequest(
          'POST',
          'checkout/me/capture',
          data,
          { 'Idempotency-Key': idempotencyKey },
          true,
        );
        return { ...axiosResponse.data, httpStatus: axiosResponse.status };
      },
      validateCapture: async (
        data: ValidateCaptureRequest,
        idempotencyKey: string,
      ): Promise<ValidateCaptureResponse> => {
        const axiosResponse = await asyncRequest(
          'POST',
          'checkout/me/validate-capture',
          data,
          { 'Idempotency-Key': idempotencyKey },
          true,
        );
        return { ...axiosResponse.data, httpStatus: axiosResponse.status };
      },
      setup3DS: async (data: Setup3DSRequest): Promise<Setup3DSResponse> => {
        const axiosResponse = await asyncRequest(
          'POST',
          `payments/in-brand/${config.countryBrand}/3ds/setup`,
          data,
          {},
          true,
        );
        return { ...axiosResponse.data, httpStatus: axiosResponse.status };
      },
      getPaymentClientToken: async (provider: 'paypal' | 'cybersource-flex'): Promise<{ clientToken: string }> =>
        asyncRequest('POST', 'checkout/me/payment-client-token', { provider }),
      getOrderDetails: async (orderId: string): Promise<unknown> => asyncRequest('GET', `orders/me/${orderId}`),
    },
  };
}
